"use client"

import { useEffect, useState } from "react"
import { useSession } from "next-auth/react"
import { usePathname } from "next/navigation"
import { X } from "lucide-react"
import { EXPIRY_WARNING_MS } from "@/lib/sessionLimits"
import { UrlLinks } from "@/shared/config"

/**
 * Warns 15 minutes before the 12-hour session end (SecureGate session_expires_at), so users can
 * save their work. "Sign in again now" asks for the password at a calm moment and starts a fresh
 * 12 hours (docs/refer/design/sessionhandling-v1.md).
 */
export function SessionExpiryWarning() {
  const { data: session, status } = useSession()
  const pathname = usePathname()
  const expiresAt = status === "authenticated" ? session?.user?.sessionExpiresAt : undefined
  const [visible, setVisible] = useState(false)
  const [dismissedFor, setDismissedFor] = useState<number | null>(null)

  useEffect(() => {
    setVisible(false)
    if (typeof expiresAt !== "number") return
    const showIn = expiresAt - EXPIRY_WARNING_MS - Date.now()
    if (Date.now() >= expiresAt) return
    if (showIn <= 0) {
      setVisible(true)
      return
    }
    const timer = setTimeout(() => setVisible(true), showIn)
    return () => clearTimeout(timer)
  }, [expiresAt])

  if (!visible || typeof expiresAt !== "number" || dismissedFor === expiresAt) return null

  const endsAt = new Date(expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 shadow-sm print:hidden"
    >
      <span>
        Your session ends at {endsAt}. Save your work; you&apos;ll need to sign in again.
      </span>
      <a
        href={`${UrlLinks.pages.reauth}?callbackUrl=${encodeURIComponent(pathname)}`}
        className="rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100"
      >
        Sign in again now
      </a>
      <button
        type="button"
        onClick={() => setDismissedFor(expiresAt)}
        className="rounded p-1 text-amber-700 hover:bg-amber-100"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
